export type ChipOption<TValue extends string> = { value: TValue; label: string };

type ChipLayout = 'wrap' | 'stack';

const chipClass = (selected: boolean, layout: ChipLayout, disabled = false) =>
    [
        'min-h-9 border text-sm transition-colors',
        disabled ? 'cursor-not-allowed opacity-40' : 'cursor-pointer',
        layout === 'stack' ? 'w-full rounded-xl px-4 py-2.5 text-left' : 'rounded-full px-3.5 py-1.5',
        selected
            ? 'border-wm-ink bg-wm-ink text-white'
            : 'border-wm-line bg-wm-surface text-wm-ink-2 hover:border-wm-ink-3',
    ].join(' ');

const groupClass = (layout: ChipLayout) => (layout === 'stack' ? 'flex flex-col gap-2' : 'flex flex-wrap gap-2');

type MultiProps<TValue extends string> = {
    options: ChipOption<TValue>[];
    values: TValue[];
    onChange: (values: TValue[]) => void;
    layout?: ChipLayout;
    /** Caps the selection; unpicked chips go disabled once the cap is reached. */
    maxValues?: number;
};

/** Multi-select chip group (filled when selected). */
export function ChipMultiSelect<TValue extends string>({
    options,
    values,
    onChange,
    layout = 'wrap',
    maxValues,
}: MultiProps<TValue>) {
    const toggle = (value: TValue) =>
        onChange(values.includes(value) ? values.filter((v) => v !== value) : [...values, value]);
    const atCap = maxValues !== undefined && values.length >= maxValues;
    return (
        <div className={groupClass(layout)}>
            {options.map((option) => {
                const selected = values.includes(option.value);
                const disabled = atCap && !selected;
                return (
                    <button
                        key={option.value}
                        type="button"
                        aria-pressed={selected}
                        disabled={disabled}
                        className={chipClass(selected, layout, disabled)}
                        onClick={() => toggle(option.value)}
                    >
                        {option.label}
                    </button>
                );
            })}
        </div>
    );
}

type SingleProps<TValue extends string> = {
    options: ChipOption<TValue>[];
    value: TValue | '';
    onChange: (value: TValue | '') => void;
    layout?: ChipLayout;
};

/** Single-select chip group; clicking the selected chip clears it. */
export function ChipSingleSelect<TValue extends string>({
    options,
    value,
    onChange,
    layout = 'wrap',
}: SingleProps<TValue>) {
    return (
        <div className={groupClass(layout)}>
            {options.map((option) => {
                const selected = value === option.value;
                return (
                    <button
                        key={option.value}
                        type="button"
                        aria-pressed={selected}
                        className={chipClass(selected, layout)}
                        onClick={() => onChange(selected ? '' : option.value)}
                    >
                        {option.label}
                    </button>
                );
            })}
        </div>
    );
}
